import React, { useState } from "react";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { SplitButton } from "@design-system/Button/SplitButton";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { HStack } from "@design-system/HStack/HStack";
import { Menu } from "@design-system/Menu/Menu.lib";
import styles from "./ButtonPlayground.module.scss";

const SAVE_DURATION_MS = 1200;

const delay = (durationMs: number) =>
    new Promise<void>((resolve) => {
        setTimeout(resolve, durationMs);
    });

/**
 * @purpose The three compositional wrappers built on Button, shown
 * live: the loader's overlay, every hotkey display position, and a split
 * primary action with its caret menu.
 */
export const ButtonWrappers = () => {
    const [isSaving, setIsSaving] = useState(false);
    const [lastAction, setLastAction] = useState("none");

    return (
        <section className={styles.matrix}>
            <h2 className={styles.matrixTitle}>Wrappers</h2>
            <div className={styles.group}>
                <h3 className={styles.groupTitle}>
                    ButtonWithLoader · last action: {lastAction}
                </h3>
                <HStack gap={12} align="center" wrap>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        loading={isSaving}
                        setLoading={setIsSaving}
                        onClick={async () => {
                            await delay(SAVE_DURATION_MS);
                            setLastAction("saved");
                        }}
                    >
                        Save
                    </ButtonWithLoader>
                    <ButtonWithLoader
                        variant="secondary"
                        size="md"
                        loading
                        onClick={() => setLastAction("never fires")}
                    >
                        Always loading
                    </ButtonWithLoader>
                    <ButtonWithLoader
                        variant="primary"
                        size="md"
                        loading={false}
                        disabled
                        tooltip="Disabled buttons still get a tooltip"
                        onClick={() => setLastAction("never fires")}
                    >
                        Disabled with tooltip
                    </ButtonWithLoader>
                </HStack>
            </div>
            <div className={styles.group}>
                <h3 className={styles.groupTitle}>WithHotkey</h3>
                <HStack gap={12} align="center" wrap>
                    <WithHotkey
                        hotKey="alt+1"
                        hotKeyOptions={{
                            description: "Playground tooltip hotkey",
                            preventDefault: true,
                        }}
                        tooltip="Runs the primary action"
                    >
                        <Button
                            variant="primary"
                            size="md"
                            onClick={() => setLastAction("alt+1")}
                        >
                            Tooltip position
                        </Button>
                    </WithHotkey>
                    <WithHotkey
                        hotKey="alt+2"
                        hotKeyOptions={{
                            description: "Playground inline hotkey",
                            preventDefault: true,
                        }}
                        hotKeyDisplayPosition="inline"
                    >
                        {(hotkeyHint) => (
                            <Button
                                variant="secondary"
                                size="md"
                                rightIcon={hotkeyHint}
                                onClick={() => setLastAction("alt+2")}
                            >
                                Inline position
                            </Button>
                        )}
                    </WithHotkey>
                    <WithHotkey
                        hotKey="alt+3"
                        hotKeyOptions={{
                            description: "Playground hidden hotkey",
                            preventDefault: true,
                        }}
                        hotKeyDisplayPosition="none"
                    >
                        <Button
                            variant="tertiary"
                            size="md"
                            onClick={() => setLastAction("alt+3")}
                        >
                            No hint
                        </Button>
                    </WithHotkey>
                </HStack>
            </div>
            <div className={styles.group}>
                <h3 className={styles.groupTitle}>SplitButton</h3>
                <HStack gap={12} align="center" wrap>
                    <SplitButton
                        onPrimaryClick={() => setLastAction("split primary")}
                        menuAriaLabel="More create options"
                        menu={
                            <>
                                <Menu.Item
                                    onClick={() =>
                                        setLastAction("split menu · first")
                                    }
                                >
                                    First option
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() =>
                                        setLastAction("split menu · second")
                                    }
                                >
                                    Second option
                                </Menu.Item>
                            </>
                        }
                    >
                        Create invoice
                    </SplitButton>
                    <SplitButton
                        variant="secondary"
                        onPrimaryClick={() => setLastAction("split secondary")}
                        menuAriaLabel="More secondary options"
                        menu={
                            <>
                                <Menu.Item
                                    onClick={() =>
                                        setLastAction("secondary menu · first")
                                    }
                                >
                                    First option
                                </Menu.Item>
                                <Menu.Item
                                    onClick={() =>
                                        setLastAction("secondary menu · second")
                                    }
                                >
                                    Second option
                                </Menu.Item>
                            </>
                        }
                    >
                        Secondary
                    </SplitButton>
                </HStack>
            </div>
        </section>
    );
};
